import type { BeanDraft, BeanPageExtractor, PageLoader } from "./beanInfo";
import { jsonLdBlocks, origin, textOf } from "./html";

/**
 * Layer 2: structured product data.
 *  - Shopify stores: GET <origin>/products/<handle>.json → title, vendor, first image, body_html.
 *  - Otherwise: schema.org Product in JSON-LD → name, brand.name, image, description.
 */
export class StructuredDataExtractor implements BeanPageExtractor {
  readonly name = "structured-data";

  async extract(url: string, html: string, loader: PageLoader): Promise<Partial<BeanDraft> | null> {
    const fromShopify = await this.shopify(url, html, loader);
    const fromJsonLd = this.jsonLd(html);
    if (!fromShopify && !fromJsonLd) return null;
    // Shopify JSON is the store's own data; prefer it, fill gaps from JSON-LD.
    return { ...(fromJsonLd ?? {}), ...(fromShopify ?? {}) };
  }

  private async shopify(url: string, html: string, loader: PageLoader): Promise<Partial<BeanDraft> | null> {
    const handle = /\/products\/([^/?#]+)/.exec(url)?.[1];
    const base = origin(url);
    if (!handle || !base || !/cdn\.shopify\.com|Shopify\.theme|shopify-section/i.test(html)) return null;
    const json = await loader(`${base}/products/${handle}.json`);
    if (!json) return null;
    let data: unknown;
    try {
      data = JSON.parse(json);
    } catch {
      return null;
    }
    const product = (data as { product?: Record<string, unknown> }).product;
    if (!product || typeof product !== "object") return null;
    const images = product["images"];
    const firstImage = Array.isArray(images) && images[0] && typeof images[0] === "object"
      ? str((images[0] as Record<string, unknown>)["src"]) : null;
    return {
      name: str(product["title"]),
      roaster: str(product["vendor"]),
      labelImageUrl: firstImage,
      description: typeof product["body_html"] === "string" ? textOf(product["body_html"]) : null,
    };
  }

  private jsonLd(html: string): Partial<BeanDraft> | null {
    for (const block of jsonLdBlocks(html)) {
      let data: unknown;
      try {
        data = JSON.parse(block);
      } catch {
        continue;
      }
      const product = findProduct(data);
      if (!product) continue;
      const brand = product["brand"];
      const image = product["image"];
      return {
        name: str(product["name"]),
        roaster: typeof brand === "string" ? brand : str((brand as Record<string, unknown> | undefined)?.["name"]),
        labelImageUrl: Array.isArray(image) ? str(image[0]) : typeof image === "object" && image
          ? str((image as Record<string, unknown>)["url"]) : str(image),
        description: typeof product["description"] === "string" ? textOf(product["description"]) : null,
      };
    }
    return null;
  }
}

function findProduct(node: unknown): Record<string, unknown> | null {
  if (Array.isArray(node)) {
    for (const n of node) { const p = findProduct(n); if (p) return p; }
    return null;
  }
  if (!node || typeof node !== "object") return null;
  const obj = node as Record<string, unknown>;
  const type = obj["@type"];
  const types = Array.isArray(type) ? type : [type];
  if (types.some((t) => typeof t === "string" && t.toLowerCase() === "product")) return obj;
  if (Array.isArray(obj["@graph"])) return findProduct(obj["@graph"]);
  return null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
}
